/**
 * PRNG seedable (mulberry32). Déterministe, sérialisable, forkable.
 * Tout ce qui dépend du hasard dans le core reçoit une instance de `Rng`.
 */
export class Rng {
  /** Graine d'origine, conservée pour la sauvegarde et le débogage. */
  readonly seed: number;
  private s: number;

  constructor(seed = 1, state?: number) {
    this.seed = seed >>> 0;
    this.s = (state ?? seed) >>> 0;
  }

  /** État interne courant (à persister pour reprendre la séquence). */
  get state(): number {
    return this.s >>> 0;
  }

  /** Flottant dans [0, 1). */
  next(): number {
    this.s = (this.s + 0x6d2b79f5) >>> 0;
    let t = this.s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Flottant dans [min, max). */
  float(min: number, max: number): number {
    return min + this.next() * (max - min);
  }

  /** Entier dans [min, max] inclus. */
  int(min: number, max: number): number {
    return min + Math.floor(this.next() * (max - min + 1));
  }

  /** Face d'un dé à `sides` faces : entier dans [1, sides]. */
  die(sides: number): number {
    return this.int(1, sides);
  }

  /** `true` avec la probabilité `p` (0..1). */
  chance(p: number): boolean {
    if (p <= 0) return false;
    if (p >= 1) return true;
    return this.next() < p;
  }

  /** Tire un élément selon des poids positifs. Renvoie `null` si tous les poids sont nuls. */
  weighted<T>(items: readonly T[], weight: (item: T) => number): T | null {
    let total = 0;
    for (const it of items) {
      const w = weight(it);
      if (w > 0) total += w;
    }
    if (total <= 0) return null;
    let r = this.next() * total;
    for (const it of items) {
      const w = weight(it);
      if (w <= 0) continue;
      r -= w;
      if (r < 0) return it;
    }
    // Garde-fou en cas d'imprécision flottante.
    return items[items.length - 1] ?? null;
  }

  /** Nouvelle instance indépendante dérivée de celle-ci (fait avancer l'état courant). */
  fork(): Rng {
    const seed = (this.next() * 0xffffffff) >>> 0;
    return new Rng(seed);
  }

  /** Copie exacte (même séquence à venir). */
  clone(): Rng {
    return new Rng(this.seed, this.s);
  }
}

/** Graine « aléatoire » pour une nouvelle partie (Math.random, sans dépendance DOM). */
export function randomSeed(): number {
  return (Math.random() * 0xffffffff) >>> 0;
}
